import type { AnswerRow, Clarification, LastTime, Question, QuestionFlag } from './api'
import type { Tone } from '../components/kit'
import { statusWord } from './status'

// The assessment review as a table (release 3.3, the mentor's point 7): what
// each row of the answers table says, decided here as plain functions so the
// node tests hold the rules and the screen only draws them.
//
// # WHY A TABLE
//
// Seventy-four questions as cards were 7,400 px of reading, every answer the
// same weight, and nothing on the page said which few a reviewer had to look
// at. The table puts the question, the answer, last time's answer, the
// evidence and one status word on a line, and the filters say how many rows
// need somebody: this round raised a finding on the answer (a rule fired, or
// the answer itself is a finding), the vendor replied to a question
// we asked, a required answer is missing, or the answer changed since last
// time. Everything else is still there under "All".
//
// # ASKED AND SKIPPED
//
// A question with a condition ("only if DH-01 is Yes") is not shown to the
// vendor when the condition is not met, and the vendor portal does not count
// it (apps/tprm-vendor-edge/internal/edge/server.go, visible()). The review
// asks the same rule, one pass, the answer of the named question against the
// listed values, so a question the vendor was never asked is "Skipped", never
// "Missing". The score still counts every question (repo/scoring_inputs.go
// keeps them all in the denominator until a declared recompute), so nothing
// here changes a number the engine produces.
//
// # A DELTA
//
// A delta reissues only the sections a trigger named, and the portal shows the
// vendor only those (sectionInScope in apps/tprm-vendor-edge/internal/edge/
// server.go). A question in another section is "Not in this round": never
// missing, never short of a file, not counted as asked.
//
// # EVIDENCE
//
// "No evidence" is the portal's own evidence gap: a question marked
// evidence-required, answered, not "not applicable", with no file. It has its
// own filter rather than counting as "needs review", because on a first round
// it is often most of the questionnaire and would bury the answers that moved;
// it is one request to the vendor, not thirty reviews.

export type Reason = 'finding' | 'reply' | 'missing' | 'changed'

export const REASON_WORDS: Record<Reason, string> = {
  finding: 'This round raised a finding on this answer',
  reply: 'The vendor replied to a question we asked',
  missing: 'Required, and not answered',
  changed: 'Different from last time',
}

export type CellText = { text: string; muted: boolean }

export type ReviewRow = {
  key: string
  question: Question
  answer?: AnswerRow
  shown: CellText
  // Where the answer came from, and the line that goes with it (the first
  // answer date of a carried answer, who asserted an internal one), so an
  // internal or carried answer never looks like one a vendor signed.
  source: { word: string; tone: Tone; note: string }
  // False for a question outside the sections a delta reissued.
  inRound: boolean
  asked: boolean
  // Why a skipped question was not asked, in words.
  skippedWhy?: string
  // Undefined when there is no earlier round to compare with.
  last?: CellText & { known: boolean }
  changed: boolean
  files: string[]
  // Null when the evidence could not be read (no evidence.read).
  evidenceGap: boolean
  flags: QuestionFlag[]
  clarification?: Clarification
  reasons: Reason[]
  status: { word: string; tone: Tone; title?: string }
}

const PROVENANCE: Record<string, { word: string; tone: Tone }> = {
  VENDOR_ATTESTED: { word: 'Attested', tone: 'ok' },
  INTERNAL_ASSERTED: { word: 'Internal', tone: 'info' },
  CARRIED_FORWARD: { word: 'Carried forward', tone: 'neutral' },
  DOCUMENT_EXTRACTED: { word: 'From document', tone: 'special' },
  UNANSWERED: { word: 'Unanswered', tone: 'plain' },
}

export function provenanceWord(answer: AnswerRow | undefined): { word: string; tone: Tone } {
  const key = (answer?.provenance || 'UNANSWERED').toUpperCase()
  return PROVENANCE[key] ?? { word: key.replace(/_/g, ' ').toLowerCase(), tone: 'neutral' }
}

// The line that goes with a provenance: when a carried answer was first given,
// who asserted an internal one, which document an extracted one came from.
export function provenanceNote(a: AnswerRow | undefined, day: (iso: string) => string): string {
  if (!a) return ''
  const key = (a.provenance || '').toUpperCase()
  if (key === 'CARRIED_FORWARD') return a.originalAnsweredAt ? 'first answered ' + day(a.originalAnsweredAt) : 'first answer date not recorded'
  if (key === 'INTERNAL_ASSERTED' && a.assertedBy) return 'by ' + a.assertedBy
  if (key === 'DOCUMENT_EXTRACTED' && a.sourceRef) return 'from ' + a.sourceRef
  return ''
}

export function isAnswered(a: { value?: string; isNa: boolean } | undefined): boolean {
  return Boolean(a && (a.isNa || (a.value !== undefined && a.value !== null && a.value !== '')))
}

// An answer in words: the option's label where the question has options.
export function answerWords(q: Question, a: { value?: string; isNa: boolean } | undefined): CellText {
  if (!a || !isAnswered(a)) return { text: 'No answer', muted: true }
  if (a.isNa) return { text: 'Not applicable', muted: true }
  const raw = a.value ?? ''
  const option = q.options?.find((o) => o.value === raw)
  return { text: option?.label || raw, muted: false }
}

// The portal's visible() rule, one pass: no condition, or the named question's
// answer is one of the listed values.
export function isAsked(q: Question, valueOf: (key: string) => string): boolean {
  const c = q.visibleWhen
  if (!c || !c.question_key) return true
  return (c.in ?? []).includes(valueOf(c.question_key))
}

function sameAnswer(a: { value?: string; isNa: boolean } | undefined, b: { value?: string; isNa: boolean } | undefined): boolean {
  const an = isAnswered(a)
  const bn = isAnswered(b)
  if (!an || !bn) return an === bn
  if (a!.isNa || b!.isNa) return a!.isNa === b!.isNa
  return (a!.value ?? '').trim() === (b!.value ?? '').trim()
}

export type ReviewInput = {
  questions: Question[]
  responses: AnswerRow[]
  lastTime?: LastTime
  questionFlags: QuestionFlag[]
  // Null when the person may not read evidence: the gap is then unknown.
  evidence: { questionKey: string; fileName: string }[] | null
  clarifications: Clarification[]
  // The sections a delta reissued; null for a full assessment.
  deltaSections: string[] | null
  // How a date is written on screen (lib/api shortDate), passed in so this
  // file stays free of the API module.
  day?: (iso: string) => string
}

// Which clarification a row shows: an unsettled round before a settled one,
// the latest round first. Unsettled is the domain's word (a rejected reply is
// still owed: domain/clarifications.go ClarificationSettled), read from the
// row's own flag rather than from a second list of statuses here.
function clarificationFor(list: Clarification[], key: string): Clarification | undefined {
  const mine = list.filter((c) => c.questionKey === key)
  const open = mine.filter((c) => !c.settled)
  const pick = (open.length > 0 ? open : mine).sort((x, y) => y.roundNo - x.roundNo)
  return pick[0]
}

// A rule that fired, or a high or critical answer finding, is red; a lower
// answer finding is amber. Red is kept for what can stop an approval.
export function findingTone(flags: QuestionFlag[]): Tone {
  const severe = flags.some((f) => f.kind === 'GATE' || f.severity === 'HIGH' || f.severity === 'CRITICAL')
  return severe ? 'crit' : 'warn'
}

export function reviewRows(input: ReviewInput): ReviewRow[] {
  const byKey = new Map(input.responses.map((r) => [r.questionKey, r]))
  // The answer text per key exactly as the portal holds it for its condition
  // check (the value even on a not-applicable answer), so the two readings
  // agree (shared fixture: apps/tprm-vendor-edge/internal/edge/testdata/
  // visible_when.json).
  const valueOf = (k: string) => byKey.get(k)?.value ?? ''
  const lastByKey = new Map((input.lastTime?.answers ?? []).map((a) => [a.questionKey, a]))
  const lastKeys = new Set(input.lastTime?.keys ?? [])
  const flagsByKey = new Map<string, QuestionFlag[]>()
  for (const f of input.questionFlags) flagsByKey.set(f.questionKey, [...(flagsByKey.get(f.questionKey) ?? []), f])
  const day = input.day ?? ((iso: string) => iso.slice(0, 10))
  const files = new Map<string, string[]>()
  for (const e of input.evidence ?? []) files.set(e.questionKey, [...(files.get(e.questionKey) ?? []), e.fileName])

  return input.questions.map((q) => {
    const answer = byKey.get(q.questionKey)
    const inRound = !input.deltaSections || input.deltaSections.length === 0 || input.deltaSections.includes(q.sectionKey)
    const asked = inRound && isAsked(q, valueOf)
    const answered = isAnswered(answer)
    const cond = q.visibleWhen
    const skippedWhy = !inRound
      ? 'Not in this round: the delta reissued other sections'
      : asked || !cond
        ? undefined
        : 'Not asked: ' + cond.question_key + (valueOf(cond.question_key) ? ' was answered ' + valueOf(cond.question_key) : ' was not answered')

    let last: ReviewRow['last']
    let changed = false
    if (input.lastTime) {
      if (!lastKeys.has(q.questionKey)) {
        last = { text: 'New question', muted: true, known: false }
      } else {
        const before = lastByKey.get(q.questionKey)
        last = { ...answerWords(q, before), known: true }
        if (!isAnswered(before)) last = { text: 'Not answered', muted: true, known: true }
        changed = asked && !sameAnswer(before, answer)
      }
    }

    const fileList = files.get(q.questionKey) ?? []
    const evidenceGap =
      input.evidence !== null && q.evidenceRequired && answered && !answer!.isNa && fileList.length === 0 && asked
    const flags = flagsByKey.get(q.questionKey) ?? []
    const clarification = clarificationFor(input.clarifications, q.questionKey)

    // A finding marks its row whether or not the vendor was shown the
    // question: the score reads every answer (repo/scoring_inputs.go), so a
    // rule can fire on a hidden or out-of-round one, and it must not hide.
    const reasons: Reason[] = []
    if (flags.length > 0) reasons.push('finding')
    if (asked) {
      if (clarification?.status === 'ANSWERED') reasons.push('reply')
      if (q.isRequired && !answered) reasons.push('missing')
      if (changed) reasons.push('changed')
    }

    const prov = provenanceWord(answer)
    const source = { word: prov.word, tone: prov.tone, note: provenanceNote(answer, day) }
    const unsettled = clarification && !clarification.settled ? statusWord('clarification', clarification.status) : null
    let status: ReviewRow['status']
    if (flags.length > 0)
      status = {
        word: 'Finding',
        tone: findingTone(flags),
        title: [...flags.map((f) => f.title || f.key), skippedWhy].filter(Boolean).join('; '),
      }
    else if (!inRound) status = { word: 'Not in this round', tone: 'plain', title: skippedWhy }
    else if (!asked) status = { word: 'Skipped', tone: 'plain', title: skippedWhy }
    else if (unsettled) status = { word: unsettled.word, tone: unsettled.tone as Tone }
    else if (reasons.includes('missing')) status = { word: 'Missing', tone: 'warn' }
    else if (changed) status = { word: 'Changed', tone: 'info' }
    else if (evidenceGap) status = { word: 'No evidence', tone: 'warn' }
    else status = { word: prov.word, tone: prov.tone }

    return {
      key: q.questionKey,
      question: q,
      answer,
      shown: answerWords(q, answer),
      source,
      inRound,
      asked,
      skippedWhy,
      last,
      changed,
      files: fileList,
      evidenceGap,
      flags,
      clarification,
      reasons,
      status,
    }
  })
}

export type ReviewView = 'review' | 'changed' | 'evidence' | 'skipped' | 'all'

export const VIEW_LABEL: Record<ReviewView, string> = {
  review: 'Needs review',
  changed: 'Changed since last time',
  evidence: 'No evidence',
  skipped: 'Skipped',
  all: 'All',
}

export function inView(row: ReviewRow, view: ReviewView): boolean {
  switch (view) {
    case 'review':
      return row.reasons.length > 0
    case 'changed':
      return row.changed
    case 'evidence':
      return row.evidenceGap
    case 'skipped':
      return row.inRound && !row.asked
    case 'all':
      return true
  }
}

// The filters a review offers, with their counts. A filter with nothing in it
// is left off, except Needs review (its zero is news) and All.
export function reviewViews(rows: ReviewRow[], hasLastTime: boolean): { key: ReviewView; label: string; count: number }[] {
  const out: { key: ReviewView; label: string; count: number }[] = []
  const count = (v: ReviewView) => rows.filter((r) => inView(r, v)).length
  out.push({ key: 'review', label: VIEW_LABEL.review, count: count('review') })
  if (hasLastTime) out.push({ key: 'changed', label: VIEW_LABEL.changed, count: count('changed') })
  for (const v of ['evidence', 'skipped'] as const) {
    const n = count(v)
    if (n > 0) out.push({ key: v, label: VIEW_LABEL[v], count: n })
  }
  out.push({ key: 'all', label: VIEW_LABEL.all, count: rows.length })
  return out
}

// The view a review opens on: what needs review when anything does, else all.
export function firstView(rows: ReviewRow[]): ReviewView {
  return rows.some((r) => r.reasons.length > 0) ? 'review' : 'all'
}

export function isView(v: string | null | undefined): v is ReviewView {
  return v === 'review' || v === 'changed' || v === 'evidence' || v === 'skipped' || v === 'all'
}

export type ReviewSection = {
  key: string
  title: string
  rows: ReviewRow[]
  asked: number
  answered: number
  needing: number
}

// The rows of a view, by questionnaire section, in the questionnaire's order.
// A section with no row in the view is left out.
export function sectionsOf(rows: ReviewRow[], view: ReviewView): ReviewSection[] {
  const order: string[] = []
  const by = new Map<string, ReviewSection>()
  for (const r of rows) {
    const k = r.question.sectionKey
    if (!by.has(k)) {
      by.set(k, { key: k, title: r.question.sectionTitle || k, rows: [], asked: 0, answered: 0, needing: 0 })
      order.push(k)
    }
    const s = by.get(k)!
    if (r.asked) s.asked++
    if (r.asked && isAnswered(r.answer)) s.answered++
    if (r.reasons.length > 0) s.needing++
    if (inView(r, view)) s.rows.push(r)
  }
  return order.map((k) => by.get(k)!).filter((s) => s.rows.length > 0)
}

// The one line under the title: how much is answered of what was asked.
export function answeredLine(rows: ReviewRow[]): string {
  if (rows.length === 0) return 'No questions'
  const asked = rows.filter((r) => r.asked)
  const answered = asked.filter((r) => isAnswered(r.answer)).length
  const outside = rows.filter((r) => !r.inRound).length
  const skipped = rows.length - asked.length - outside
  const parts = [answered + ' of ' + asked.length + ' answered']
  if (skipped > 0) parts.push(skipped + ' skipped by an earlier answer')
  if (outside > 0) parts.push(outside + ' not in this round')
  return parts.join(', ')
}
